import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { FaEnvelope, FaPhone, FaLinkedin, FaInstagram, FaMapMarkerAlt } from 'react-icons/fa';
import { SiLinktree } from 'react-icons/si';

const Contact = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const contactInfo = [
    {
      icon: <FaEnvelope />,
      label: "Email",
      value: "vrajupadhyay12@gmail.com",
      link: "mailto:vrajupadhyay12@gmail.com"
    },
    {
      icon: <FaPhone />,
      label: "Phone",
      value: "+91 72020 45251",
      link: "tel:+917202045251"
    },
    {
      icon: <FaMapMarkerAlt />,
      label: "Location",
      value: "Anand, Gujarat, India",
      link: null
    }
  ];

  const socialLinks = [
    {
      icon: <FaLinkedin />,
      label: "LinkedIn",
      url: "https://linkedin.com/in/vrajupadhyay",
      color: "#0A66C2"
    },
    {
      icon: <FaInstagram />,
      label: "Instagram",
      url: "https://instagram.com/vraj_upadhyay12",
      color: "#E4405F"
    },
    {
      icon: <SiLinktree />,
      label: "Linktree",
      url: "https://linktr.ee/vrajupadhyay",
      color: "#39E09B"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <section id="contact" className="section-padding section-dark">
      <div className="container">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 0.6 }}
        >
          <div className="section-header">
            <h2 className="section-title">Let's Connect</h2>
            <div className="section-divider"></div>
            <p className="section-description">
              Have a project in mind? Let's work together to bring your ideas to life
            </p>
          </div>

          <div className="contact-content">
            <motion.div
              className="contact-info-grid"
              variants={containerVariants}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
            >
              {contactInfo.map((info, index) => (
                <motion.div
                  key={index}
                  className="contact-info-card"
                  variants={itemVariants}
                  initial={{ scale: 0, rotate: -180 }}
                  animate={isInView ? { scale: 1, rotate: 0 } : { scale: 0, rotate: -180 }}
                  transition={{
                    type: "spring",
                    stiffness: 100,
                    damping: 12,
                    delay: index * 0.15
                  }}
                  whileHover={{ 
                    y: -10,
                    scale: 1.05,
                    boxShadow: "0 15px 35px rgba(99, 102, 241, 0.3)",
                    transition: { type: "spring", stiffness: 300, damping: 15 }
                  }}
                >
                  <motion.div 
                    className="contact-icon"
                    animate={{
                      scale: [1, 1.1, 1],
                      rotate: [0, 5, -5, 0]
                    }}
                    transition={{
                      repeat: Infinity,
                      duration: 2,
                      delay: index * 0.3
                    }}
                    whileHover={{
                      scale: 1.3,
                      rotate: 360,
                      transition: { duration: 0.5 }
                    }}
                  >
                    {info.icon}
                  </motion.div>
                  <motion.h4 
                    className="contact-label"
                    initial={{ opacity: 0 }}
                    animate={isInView ? { opacity: 1 } : { opacity: 0 }}
                    transition={{ delay: index * 0.15 + 0.2 }}
                  >
                    {info.label}
                  </motion.h4>
                  {info.link ? (
                    <motion.a 
                      href={info.link} 
                      className="contact-value"
                      initial={{ opacity: 0 }}
                      animate={isInView ? { opacity: 1 } : { opacity: 0 }}
                      transition={{ delay: index * 0.15 + 0.3 }}
                      whileHover={{ color: "var(--primary)", scale: 1.05 }}
                    >
                      {info.value}
                    </motion.a>
                  ) : (
                    <motion.p 
                      className="contact-value"
                      initial={{ opacity: 0 }}
                      animate={isInView ? { opacity: 1 } : { opacity: 0 }}
                      transition={{ delay: index * 0.15 + 0.3 }}
                    >
                      {info.value}
                    </motion.p>
                  )}
                </motion.div>
              ))}
            </motion.div>

            <motion.div
              className="contact-social"
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <h3 className="social-title">Connect with me on social media</h3>
              <div className="social-buttons">
                {socialLinks.map((social, index) => (
                  <motion.a
                    key={index}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-button"
                    style={{ '--social-color': social.color }}
                    initial={{ opacity: 0, x: -50 }}
                    animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
                    transition={{
                      delay: 0.3 + index * 0.1,
                      type: "spring",
                      stiffness: 100
                    }}
                    whileHover={{ 
                      scale: 1.15, 
                      y: -8,
                      rotate: [0, -2, 2, 0],
                      boxShadow: `0 10px 30px ${social.color}40`,
                      transition: { type: "spring", stiffness: 400, damping: 10 }
                    }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <motion.span 
                      className="social-button-icon"
                      whileHover={{
                        rotate: 360,
                        transition: { duration: 0.5 }
                      }}
                    >
                      {social.icon}
                    </motion.span>
                    <span className="social-button-label">{social.label}</span>
                  </motion.a>
                ))}
              </div>
            </motion.div>

            <motion.div
              className="instagram-embed"
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <h3 className="social-title">
                <FaInstagram style={{ color: '#E4405F', marginRight: '0.5rem' }} />
                Follow My Journey on Instagram
              </h3>
              <p className="section-description" style={{ marginBottom: '1.5rem' }}>
                Daily updates, tech insights, and behind-the-scenes moments
              </p>
              <div className="instagram-container">
                <div className="instagram-frame">
                  <iframe
                    src="https://www.instagram.com/vraj_upadhyay12/embed"
                    width="100%"
                    height="580"
                    frameBorder="0"
                    scrolling="no"
                    allowTransparency="true"
                    title="Instagram Profile - Vraj Upadhyay"
                  ></iframe>
                </div>
                <motion.a
                  href="https://instagram.com/vraj_upadhyay12"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="instagram-follow-btn"
                  whileHover={{ 
                    scale: 1.08,
                    y: -3,
                    boxShadow: "0 10px 30px rgba(228, 64, 95, 0.4)",
                    transition: { type: "spring", stiffness: 400, damping: 10 }
                  }}
                  whileTap={{ scale: 0.95 }}
                  animate={{
                    boxShadow: [
                      "0 5px 15px rgba(228, 64, 95, 0.2)",
                      "0 8px 25px rgba(228, 64, 95, 0.3)",
                      "0 5px 15px rgba(228, 64, 95, 0.2)"
                    ]
                  }}
                  transition={{
                    repeat: Infinity,
                    duration: 2
                  }}
                >
                  <motion.span
                    animate={{
                      rotate: [0, 10, -10, 0],
                      scale: [1, 1.1, 1]
                    }}
                    transition={{
                      repeat: Infinity,
                      duration: 3
                    }}
                  >
                    <FaInstagram />
                  </motion.span>
                  {" "}Follow @vraj_upadhyay12
                </motion.a>
              </div>
            </motion.div>

            <motion.div
              className="contact-cta"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <motion.a
                href="mailto:vrajupadhyay12@gmail.com"
                className="btn btn-primary btn-large"
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ delay: 0.6, type: "spring", stiffness: 100 }}
                whileHover={{ 
                  scale: 1.08,
                  y: -5,
                  boxShadow: "0 15px 40px rgba(99, 102, 241, 0.4)",
                  transition: { type: "spring", stiffness: 400, damping: 10 }
                }}
                whileTap={{ scale: 0.95 }}
              >
                Send Me a Message
              </motion.a>
              <motion.a
                href="/Vraj's Resume.pdf"
                download="Vraj_Upadhyay_Resume.pdf"
                className="btn btn-secondary btn-large"
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ delay: 0.7, type: "spring", stiffness: 100 }}
                whileHover={{ 
                  scale: 1.08,
                  y: -5,
                  boxShadow: "0 15px 40px rgba(139, 92, 246, 0.4)",
                  transition: { type: "spring", stiffness: 400, damping: 10 }
                }}
                whileTap={{ scale: 0.95 }}
              >
                Download Resume
              </motion.a>
              <motion.a
                href="https://calendly.com/vrajupadhyay12"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-accent btn-large"
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ delay: 0.8, type: "spring", stiffness: 100 }}
                whileHover={{ 
                  scale: 1.08,
                  y: -5,
                  boxShadow: "0 15px 40px rgba(236, 72, 153, 0.4)",
                  transition: { type: "spring", stiffness: 400, damping: 10 }
                }}
                whileTap={{ scale: 0.95 }}
              >
                Schedule a Meeting
              </motion.a>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
